import * as z from "zod";
import {
  FlowContentNodeSchema,
  type FlowContentNode,
} from "../flow-content-node-schema";
import {
  BranchLockAttrsSchema,
  ConditionalLockAttrsSchema,
  StepLockAttrsSchema,
} from "../lock-types-schema";
import { ConditionSchema, type Condition } from "./condition-schema";

/**
 * Branch type: if, else-if, or else.
 */
export const branchTypes = ["if", "else-if", "else"] as const;
export type BranchType = (typeof branchTypes)[number];

/**
 * Step within a conditional branch.
 */
const ConditionalStepSchema_ = z.object({
  label: z.string(),
  type: z.string().optional(),
  content: z.array(z.lazy(() => FlowContentNodeSchema)),
  selectionKey: z.string().optional(),
  selectionLabel: z.string().optional(),
  itemKey: z.string().optional(),
  itemLabel: z.string().optional(),
  lock: StepLockAttrsSchema.optional(),
});

/** Base type from schema inference (no index signature) */
type ConditionalStepBase = Omit<
  z.infer<typeof ConditionalStepSchema_>,
  "content"
> & { content: FlowContentNode[] };

/** Full type with index signature for FlowContentNode compatibility */
export type ConditionalStep = ConditionalStepBase & { [key: string]: unknown };

export const ConditionalStepSchema: z.ZodType<ConditionalStep> =
  ConditionalStepSchema_;

/**
 * Branch within a conditional (if/else-if/else).
 */
const ConditionalBranchSchema_ = z.object({
  type: z.enum(branchTypes),
  condition: ConditionSchema.optional(),
  steps: z.array(ConditionalStepSchema_),
  lock: BranchLockAttrsSchema.optional(),
});

/** Base type from schema inference (no index signature) */
type ConditionalBranchBase = Omit<
  z.infer<typeof ConditionalBranchSchema_>,
  "steps" | "condition"
> & { condition?: Condition; steps: ConditionalStep[] };

/** Full type with index signature for FlowContentNode compatibility */
export type ConditionalBranch = ConditionalBranchBase & {
  [key: string]: unknown;
};

export const ConditionalBranchSchema: z.ZodType<ConditionalBranch> =
  ConditionalBranchSchema_;

/**
 * Conditional node with multiple branches.
 */
const ConditionalNodeSchema_ = z.object({
  type: z.literal("conditional"),
  branches: z.array(ConditionalBranchSchema_),
  lock: ConditionalLockAttrsSchema.optional(),
});

/** Base type from schema inference (no index signature) */
type ConditionalNodeBase = Omit<
  z.infer<typeof ConditionalNodeSchema_>,
  "branches"
> & { branches: ConditionalBranch[] };

/** Full type with index signature for FlowContentNode compatibility */
export type ConditionalNode = ConditionalNodeBase & { [key: string]: unknown };

export const ConditionalNodeSchema: z.ZodType<ConditionalNode> =
  ConditionalNodeSchema_;

/**
 * Type guard for conditional nodes.
 */
export function isConditionalNode(node: unknown): node is ConditionalNode {
  return (
    typeof node === "object" &&
    node !== null &&
    (node as Record<string, unknown>).type === "conditional"
  );
}
